import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

/**
 * Forced + voluntary password rotation page.
 *
 * Two entry points share this screen:
 *
 *   1. **Forced** — admin-created accounts land here on first login
 *      because `must_change_password=true` flips the
 *      `mustChangePassword` flag in `AuthContext`. `ProtectedRoute`
 *      redirects every protected route to `/change-password` until
 *      the flag clears.
 *
 *   2. **Voluntary** — any logged-in user can navigate here to
 *      rotate their password. Same form, same validation.
 *
 * Strength rules mirror the server-side gate in
 * `routes/auth.py::_validate_password_strength` so the user gets
 * real-time feedback that won't surprise them at submit time.
 */
const RULES = [
  { id: 'len', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'alpha', label: 'Contains a letter', test: (p) => /[a-zA-Z]/.test(p) },
  { id: 'mixed', label: 'Contains a number or symbol', test: (p) => /[^a-zA-Z]/.test(p) },
];

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading, mustChangePassword, changePassword, user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // If the user lands here unauthenticated (deep-link, expired session)
  // bounce to login. We don't redirect away on success here — let
  // the user click "Continue to dashboard" so they see the success
  // state explicitly.
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  const ruleResults = RULES.map((r) => ({ ...r, ok: r.test(newPassword) }));
  const allRulesPass = ruleResults.every((r) => r.ok);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = (
    !submitting
    && currentPassword.length > 0
    && allRulesPass
    && passwordsMatch
    && newPassword !== currentPassword
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Password updated. You can use it on your next sign-in.');
      // Forced flow → land them on the dashboard. Voluntary flow →
      // bounce them back to where they came from. We don't track the
      // referrer so the dashboard is a fine universal fallback.
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Password change failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4"
      data-testid="change-password-page"
    >
      <Card className="w-full max-w-md bg-white border border-slate-200 backdrop-blur shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-full bg-teal-50 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-teal-600" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            {mustChangePassword ? 'Set your password' : 'Change password'}
          </CardTitle>
          <CardDescription>
            {mustChangePassword
              ? `Welcome${user?.name ? `, ${user.name}` : ''}. Choose a password only you know — your temporary one expires now.`
              : 'Update the password used to sign in to your account.'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">
                {mustChangePassword ? 'Temporary password' : 'Current password'}
              </label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="pl-10 pr-10 h-11"
                  data-testid="current-password-input"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Toggle current password visibility"
                >
                  {showCurrent ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">New password</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 pr-10 h-11"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Toggle new password visibility"
                >
                  {showNew ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Confirm new password</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 pr-10 h-11"
                  data-testid="confirm-password-input"
                />
              </div>
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-xs text-rose-600 mt-1">Passwords don't match.</p>
              )}
            </div>

            <ul className="text-xs space-y-1 text-slate-600" data-testid="password-rules-list">
              {ruleResults.map((r) => (
                <li
                  key={r.id}
                  className={r.ok ? 'text-teal-700' : 'text-slate-500'}
                  data-testid={`password-rule-${r.id}`}
                >
                  <span aria-hidden>{r.ok ? '✓' : '○'}</span> {r.label}
                </li>
              ))}
              {newPassword.length > 0 && newPassword === currentPassword && (
                <li className="text-rose-600">○ Must differ from your current password</li>
              )}
            </ul>

            <Button
              type="submit"
              className="w-full h-11 bg-teal-600 hover:bg-teal-700"
              disabled={!canSubmit}
              data-testid="change-password-submit-btn"
            >
              {submitting
                ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                : (mustChangePassword ? 'Set password & continue' : 'Update password')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
